import React from 'react';

import { TeamNanoID } from '../../../../../../teams/teamsTypes';
import { ThreeDStockProductsListItemButtonsProduct } from './ThreeDStockProductsListItemButtons.types';
import {
  OnProductSimilarSearchAction,
  UpdateProductCacheAction
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { DownloadProductButton } from '../../../../../../products/components/buttons/DownloadProductButton';
import { DownloadProductSeparatelyButton } from '../../../../../../products/components/buttons/DownloadProductSeparatelyButton';
import { ProductFavoriteButton } from '../../../../../../products/components/buttons/ProductFavoriteButton';
import { ProductsCopyLinkButton } from '../../../../../../products/components/buttons/ProductsCopyLinkButton';
import { ProductsListItemSimilarSearchButton } from '../../../../../../products/components/lists/ProductsListItem/components/ProductsListItemSimilarSearchButton';
import { SubmitProductToProjectOrTaskModalButton } from '../../../../../../products/components/buttons/SubmitProductToProjectOrTaskModalButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';

import { ProductsPermissions } from '../../../../../../products/productsConstants';
import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { aiSearchKeys, words } from '../../../../../../../locales/keys';

interface ThreeDStockProductsListItemButtonsProps {
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  onEditButtonMouseEnter?: () => void;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  product: ThreeDStockProductsListItemButtonsProduct;
  productEditPagePath: string;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsListItemButtonsProduct>;
  companyNanoId?: TeamNanoID;
  editButtonAction?: string;
}

function ThreeDStockProductsListItemButtons({
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  onEditButtonMouseEnter,
  onProductSimilarSearchFilter,
  product,
  productEditPagePath,
  updateProductCache,
  companyNanoId,
  editButtonAction = ProductsPermissions.READ_PRODUCT_EDIT_BUTTON
}: ThreeDStockProductsListItemButtonsProps) {
  return (
    <div className="flex justify-between gap-1">
      <div className="min-w-0">
        <CheckPermissions
          action={ProductsPermissions.READ_PRODUCT_FAVORITE_BUTTON}
        >
          <ProductFavoriteButton
            dataGa="products-product-favorite-button"
            product={product}
            icon={
              product.favorite ? IconsEnum.HEART_SOLID : IconsEnum.HEART_OUTLINE
            }
            iconClassName="h-5 w-5 shrink-0"
            i18nText={product.favorite ? words.inFavorites : words.toFavorites}
            i18nTextClassName="truncate"
            updateIndexProductCache={updateProductCache}
            className={
              product.favorite
                ? 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-pink-500 hover:bg-pink-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
                : 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-black hover:bg-gray-900 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
          />
        </CheckPermissions>
      </div>

      <div className="flex gap-1">
        <CheckPermissions action={downloadButtonAction}>
          <DownloadProductButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="products-product-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            productUuid={product.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions action={downloadSeparatelyButtonAction}>
          <DownloadProductSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="products-product-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            productUuid={product.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          <CheckPermissions
            action={
              ProductsPermissions.READ_PRODUCT_SUBMIT_TO_TASK_OR_PROJECT_BUTTON
            }
          >
            <SubmitProductToProjectOrTaskModalButton
              dataGa="products-product-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              product={product}
              i18nText={words.attach}
              i18nTextClassName="inline"
              iconClassName="w-5 h-5"
            />
          </CheckPermissions>

          <CheckPermissions action={findSimilarButtonAction}>
            <ProductsListItemSimilarSearchButton
              dataGa="products-product-similar-search-filter-button"
              onProductSimilarSearch={onProductSimilarSearchFilter}
              product={product}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              i18nText={aiSearchKeys.findSimilar}
              i18nTextClassName="inline"
              icon={IconsEnum.SEARCH_AI_SOLID}
            />
          </CheckPermissions>

          <CheckPermissions
            action={ProductsPermissions.READ_PRODUCT_COPY_LINK_BUTTON}
          >
            <ProductsCopyLinkButton
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              dataGa="products-product-copy-link-button"
              icon={IconsEnum.LINK_SOLID}
              iconClassName="h-5 w-5"
              product={product}
              i18nText={words.copyLink}
              i18nTextClassName="inline"
              companyNanoId={companyNanoId}
            />
          </CheckPermissions>

          <CheckPermissions action={editButtonAction}>
            <NextLinkHelper
              onMouseEnter={onEditButtonMouseEnter}
              dataGa="products-product-edit-link"
              href={productEditPagePath}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            >
              <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
              <span>
                <Translate id={words.edit} />
              </span>
            </NextLinkHelper>
          </CheckPermissions>
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockProductsListItemButtons;
